import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Form, Button, Container, Alert, Row, Col } from 'react-bootstrap'
import { inventoryItemAPI } from '../api/inventoryItemAPI'
import { warehouseAPI } from '../api/warehouseAPI'
import { locationAPI } from '../api/locationAPI'
import { stockItemAPI } from '../api/stockItemAPI'
import { vendorAPI } from '../api/vendorAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import StockItemCard from '../components/StockItemCard'
import QuantityPicker from '../components/QuantityPicker'
import CostHistoryDisplay from '../components/CostHistoryDisplay'
import { usePermissions } from '../contexts/PermissionContext'
import Select from 'react-select'


function InventoryReceiveScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const navigate = useNavigate()
    const location = useLocation()
    const { stockItemId } = useParams()
    const [error, setError] = useState(null)
    const [warehouses, setWarehouses] = useState([])
    const [locations, setLocations] = useState([])
    const [inventoryItem, setInventoryItem] = useState({
        stock_item: '',
        warehouse: { _id: '' },
        location: '',
        quantity: '',
        lot: '',
        notes: '',
        bb_date: '',
        vendor: '',
        cost: '',
    })
    const [availableVendors, setAvailableVendors] = useState([])

    // Get initial data if coming from vendor confirmation
    const initialData = location.state?.initialData
    const confirmedPoLineId = location.state?.confirmedPoLineId
    const fromVendorConfirmation = location.state?.fromVendorConfirmation
    const poId = location.state?.poId
    const fromScanner = location.state?.fromScanner

    useEffect(() => {
        let isMounted = true;

        async function fetchStockItem() {
            try {
                const stockItem = await stockItemAPI.getById(stockItemId)
                if (!isMounted) return;
                
                setInventoryItem(prevState => ({
                    ...prevState,
                    stock_item: stockItemId,
                    stockItemData: stockItem,
                    // Apply initial data if available (but not quantity to force manual entry)
                    ...(initialData && {
                        vendor: initialData.vendor || prevState.vendor,
                        warehouse: {
                            _id: initialData.warehouse || prevState.warehouse._id
                        },
                        bb_date: initialData.bb_date || prevState.bb_date,
                        lot: initialData.lot || prevState.lot
                    })
                }))
            } catch (error) {
                if (!isMounted) return;
                console.error('Error fetching stock item:', error)
                setError('Failed to load stock item')
            }
        }

        if (stockItemId) {
            fetchStockItem()
        }

        return () => {
            isMounted = false;
        }
    }, [stockItemId, initialData])

    useEffect(() => {
        let isMounted = true;

        async function fetchWarehouses() {
            try {
                const response = await warehouseAPI.getAll()
                if (!isMounted) return;
                setWarehouses(response)
            } catch (error) {
                if (!isMounted) return;
                console.error('Error fetching warehouses:', error)
                setError('Failed to load warehouses')
            }
        }
        
        fetchWarehouses()

        return () => {
            isMounted = false;
        }
    }, [])

    useEffect(() => {
        let isMounted = true;

        async function fetchLocations() {
            try {
                if (!inventoryItem.warehouse._id) {
                    setLocations([])
                    return;
                }
                
                const response = await locationAPI.getAll(inventoryItem.warehouse._id)
                if (!isMounted) return;
                
                setLocations(response)
                
                // Find and set the first location that ends with "-FLOOR"
                const floorLocation = response.find(loc => loc.name.endsWith('-FLOOR'))
                if (floorLocation && isMounted) {
                    setInventoryItem(prev => ({
                        ...prev,
                        location: floorLocation._id
                    }))
                }
            } catch (error) {
                if (!isMounted) return;
                console.error('Error fetching locations:', error)
                setError('Failed to load locations')
            }
        }
        
        fetchLocations()

        return () => {
            isMounted = false;
        }
    }, [inventoryItem.warehouse._id]) // Changed dependency to just the ID

    useEffect(() => {
        let isMounted = true;

        async function fetchVendors() {
            try {
                const vendors = await vendorAPI.getAll()
                if (!isMounted) return;
                setAvailableVendors(vendors)
            } catch (error) {
                if (!isMounted) return;
                console.error('Error fetching vendors:', error)
                setError('Failed to load vendors')
            }
        }
        
        fetchVendors()

        return () => {
            isMounted = false;
        }
    }, [])

    const submitHandler = async (e) => {
        e.preventDefault()
        try {
            // Add confirmed PO line ID to the request if available
            const requestData = {
                ...inventoryItem,
                stock_item: inventoryItem.stock_item,
                warehouse: inventoryItem.warehouse._id,
                ...(confirmedPoLineId && { confirmed_po_line_id: confirmedPoLineId })
            }
            
            await inventoryItemAPI.create(requestData)
            
            // Check if user came from vendor confirmation page
            if (fromVendorConfirmation && poId) {
                // Navigate back to the PO details page (vendor confirmations)
                navigate(`/purchase-order/${poId}`, {
                    state: { 
                        update: true,
                        showVendorConfirmations: true 
                    }
                })
            } else {
                // Default behavior: navigate back to inventory with the filters preserved and refresh trigger
                const returnFilters = location.state?.returnFilters || ''
                
                // Add refresh parameter to trigger data refresh
                const separator = returnFilters.includes('?') ? '&' : '?'
                const refreshParam = `${separator}refresh=1`
                
                navigate(`/inventory${returnFilters}${refreshParam}`)
            }
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    const formatWarehouseOptions = () => {
        return warehouses.map(warehouse => ({
            value: warehouse._id,
            label: warehouse.name
        }))
    }

    const formatLocationOptions = () => {
        return locations.map(location => ({
            value: location._id,
            label: location.name
        }))
    }

    if (permissionsLoading) {
        return (
            <Container className="py-3">
                <div className="text-center">Loading...</div>
            </Container>
        )
    }

    if (!hasPermission('inventory.receive')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to receive inventory items.
                </Alert>
            </Container>
        )
    }

    return (
        <Container>
            <Row className="justify-content-left">
            <Col style={{ maxWidth: '730px' }}>
            <h1>Receive</h1>
            {inventoryItem.stockItemData && (
                <StockItemCard stockItem={inventoryItem.stockItemData} />
            )}
            <div style={{ height: '20px' }}></div>
            <Form onSubmit={submitHandler}>
                <Form.Group className="mb-4" controlId="warehouse">
                    <Form.Label>Warehouse*</Form.Label>
                    <Select
                        value={formatWarehouseOptions().find(
                            option => option.value === inventoryItem.warehouse._id
                        )}
                        onChange={(selectedOption) => setInventoryItem({
                            ...inventoryItem,
                            warehouse: { _id: selectedOption.value }
                        })}
                        options={formatWarehouseOptions()}
                        isSearchable
                        placeholder="Select Warehouse"
                        className="react-select-container"
                        classNamePrefix="react-select"
                    />
                </Form.Group>
                <Form.Group className="mb-4" controlId="location">
                    <Form.Label>Location*</Form.Label>
                    <Select
                        value={formatLocationOptions().find(
                            option => option.value === inventoryItem.location
                        )}
                        onChange={(selectedOption) => setInventoryItem({
                            ...inventoryItem,
                            location: selectedOption ? selectedOption.value : ''
                        })}
                        options={formatLocationOptions()}
                        isSearchable
                        placeholder="Select Location"
                        className="react-select-container"
                        classNamePrefix="react-select"
                        isDisabled={!inventoryItem.warehouse._id}
                    />
                </Form.Group>
                <Form.Group className="mb-4" controlId="lot">
                    <Form.Label>LOT</Form.Label>
                    <Form.Control
                        type="text"
                        value={inventoryItem.lot}
                        onChange={(e) => setInventoryItem({...inventoryItem, lot: e.target.value})}
                        placeholder="Enter LOT"
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Form.Group>
                <Form.Group className="mb-4" controlId="vendor">
                    <Form.Label>Vendor*</Form.Label>
                    <Select
                        value={availableVendors.find(v => v._id === inventoryItem.vendor)
                            ? { value: inventoryItem.vendor, label: availableVendors.find(v => v._id === inventoryItem.vendor).name }
                            : null}
                        onChange={(selectedOption) => setInventoryItem({
                            ...inventoryItem,
                            vendor: selectedOption ? selectedOption.value : ''
                        })}
                        options={availableVendors.map(vendor => ({
                            value: vendor._id,
                            label: vendor.name
                        }))}
                        isSearchable
                        required
                        placeholder="Select Vendor"
                        className="react-select-container"
                        classNamePrefix="react-select"
                    />
                </Form.Group>
                <Form.Group className="mb-4" controlId="cost">
                    <Form.Label>Cost*</Form.Label>
                    <Form.Control
                        type="number"
                        min="0"
                        step="0.0001"
                        value={inventoryItem.cost}
                        onChange={(e) => setInventoryItem({...inventoryItem, cost: e.target.value})}
                        required
                        placeholder="Enter cost"
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Form.Group>

                {/* Display cost history for this stock item */}
                <CostHistoryDisplay stockItemId={stockItemId} />

                <Form.Group className="mb-4" controlId="bb_date">
                    <Form.Label>Best Before Date</Form.Label>
                    <Form.Control
                        type="date"
                        value={inventoryItem.bb_date || ''}
                        onChange={(e) => setInventoryItem({...inventoryItem, bb_date: e.target.value})}
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Form.Group>
                <Form.Label>Quantity*</Form.Label>
                <QuantityPicker
                    casePack={inventoryItem.stockItemData?.case_pack || 1}
                    uom={inventoryItem.stockItemData?.uom || ''}
                    value={inventoryItem.quantity}
                    onChange={(qty) => setInventoryItem(prev => ({...prev, quantity: qty}))}
                    required
                />
                <Form.Group className="mb-4" controlId="notes">
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={inventoryItem.notes}
                        onChange={(e) => setInventoryItem({...inventoryItem, notes: e.target.value})}
                        placeholder="Enter notes"
                        autoComplete="off"
                        spellCheck="true"
                    />
                </Form.Group>
                {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible>
                        {error}
                    </Alert>
                )}
                <Button variant="success" type="submit" className="me-2">
                    Receive
                </Button>
                <Button 
                    variant="outline-primary" 
                    onClick={() => navigate(-1)}
                >
                    {!fromScanner && <i className="fas fa-arrow-left me-2"></i>}
                    {fromScanner ? 'Cancel' : 'Back to List'}
                </Button>
            </Form>
            </Col>
            </Row>
        </Container>
    )
}

export default InventoryReceiveScreen 